import type { I18n } from '@learnforge/i18n';
import { snapToSpecialRadian, radToDeg } from '../trig/angles';
import { createSvgEl } from '@learnforge/demo-render';

export interface AngleArcOptions {
  cx: number;
  cy: number;
  r: number;
  startAngle: number;
  endAngle: number;
  showLabel?: boolean;
  labelUnit?: 'deg' | 'rad' | 'both';
  i18n: I18n<string>;
}

export interface AngleArcHandle {
  group: SVGGElement;
  update(opts: Partial<AngleArcOptions>): void;
  destroy(): void;
}

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
  const sweep = a1 > a0 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
}

function formatRadian(theta: number): string {
  const snap = snapToSpecialRadian(theta);
  if (snap) {
    if (snap.num === 0) return '0';
    if (snap.num === snap.den) return 'π';
    if (snap.den === 1) return `${snap.num}π`;
    if (snap.num === 1) return `π/${snap.den}`;
    return `${snap.num}π/${snap.den}`;
  }
  return theta.toFixed(2);
}

function formatDegree(theta: number): string {
  return `${Math.round(radToDeg(theta))}°`;
}

export function createAngleArc(parent: SVGGElement, opts: AngleArcOptions): AngleArcHandle {
  const group = createSvgEl('g', { class: 'tg-arc' });
  parent.appendChild(group);
  let current: AngleArcOptions = { ...opts };

  function render() {
    while (group.firstChild) group.removeChild(group.firstChild);
    const path = createSvgEl('path', {
      d: arcPath(current.cx, current.cy, current.r, current.startAngle, current.endAngle),
      class: 'tg-arc-path',
      'vector-effect': 'non-scaling-stroke',
    });
    group.appendChild(path);
    if (current.showLabel) {
      const sweep = current.endAngle - current.startAngle;
      const mid = current.startAngle + sweep / 2;
      const lx = current.cx + (current.r + 0.35) * Math.cos(mid);
      const ly = current.cy + (current.r + 0.35) * Math.sin(mid);
      const text = createSvgEl('text', {
        class: 'tg-arc-label',
        x: lx,
        y: -ly,
        'text-anchor': 'middle',
        'dominant-baseline': 'middle',
        transform: 'scale(1 -1)',
      });
      const unit = current.labelUnit ?? 'deg';
      const value = current.endAngle - current.startAngle;
      if (unit === 'deg') {
        text.textContent = formatDegree(value);
      } else if (unit === 'rad') {
        text.textContent = formatRadian(value);
      } else {
        text.textContent = `${formatDegree(value)} = ${formatRadian(value)}`;
      }
      group.appendChild(text);
    }
  }
  render();

  return {
    group,
    update(next) {
      current = { ...current, ...next };
      render();
    },
    destroy() {
      group.remove();
    },
  };
}
